import React, { ChangeEvent, useEffect, useState } from "react";
import * as Yup from "yup";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import { useNavigate } from "react-router-dom";
const PHONE_PREFIX = import.meta.env.VITE_PHONE_PREFIX || "+221";
const PHONE_LENGTH = Number(import.meta.env.VITE_PHONE_LENGTH) || 9;
import { UserModel } from "@/app/entities/user.model";
import {
  UiAdvancedSelect,
  UiButton,
  UiDatepicker,
  UiInput,
} from "@/components/ui";
import { useYupValidationErrors } from "@/utils";
import { useCities } from "@/composables/cities";
import { useToast } from "@/composables/toast";
import { UserTypes, useUsers } from "@/composables/users";

// Calcul des bornes de dates
const today = new Date();

const minBirth = new Date(today);
minBirth.setFullYear(today.getFullYear() - 100);

const maxBirth = new Date(today);
maxBirth.setFullYear(today.getFullYear() - 18); // 18 ans minimum

const formatDateToFrench = (date: string | Date, f: string = "dd/MM/yyyy") => {
  if (!date) return "";
  return format(new Date(date), f, { locale: fr });
};

const formatDateToDB = (date: string | Date, f: string = "yyyy-MM-dd") => {
  if (!date) return "";
  return format(new Date(date), f, { locale: fr });
};

// Validation du schéma
const validationSchema = (villes: string[]) =>
  Yup.object().shape({
    type: Yup.string().required("Le type de profil est obligatoire"),
    nom: Yup.string().when("type", {
      is: (val: string) => val === UserTypes.ENTREPRISE,
      then: (schema) =>
        schema
          .required("Le nom de l'entreprise est obligatoire")
          .min(2, "Le nom de l'entreprise doit contenir au moins 2 caractères")
          .max(50, "Le nom de l'entreprise ne peut pas dépasser 50 caractères"),
      otherwise: (schema) =>
        schema
          .required("Le nom est obligatoire")
          .min(2, "Le nom doit contenir au moins 2 caractères")
          .max(50, "Le nom ne peut pas dépasser 50 caractères"),
    }),
    prenom: Yup.string()
      .when("type", {
        is: UserTypes.PARTICULIER,
        then: (schema) =>
          schema
            .required("Le prénom est obligatoire")
            .min(2, "Le prénom doit contenir au moins 2 caractères")
            .max(50, "Le prénom ne peut pas dépasser 50 caractères"),
      })
      .when("type", {
        is: UserTypes.ENTREPRISE,
        then: (schema) =>
          schema
            .required("Le NINEA est obligatoire")
            .min(2, "Le NINEA doit contenir au moins 2 caractères")
            .max(15, "Le NINEA ne peut pas dépasser 15 caractères"),
      }),
    email: Yup.string()
      .required("L'email est obligatoire")
      .email("L'email n'est pas valide"),

    phone: Yup.string().required("Le téléphone est obligatoire"),

    dateNaissance: Yup.date().when("type", {
      is: (val: string) => val === UserTypes.PARTICULIER,
      then: (schema) =>
        schema
          .required("La date de naissance est obligatoire")
          .min(
            minBirth,
            `La date de naissance doit être après le ${minBirth.toLocaleDateString(
              "fr-FR"
            )}`
          )
          .max(maxBirth, `Vous devez avoir au moins 18 ans pour vous inscrire`),
    }),
    adresse: Yup.object()
      .shape({
        pays: Yup.string(),
        ville: Yup.string(),
        intituleRue: Yup.string().required("L'adresse est obligatoire"),
        numeroRue: Yup.number().nullable(),
      })
      .when("type", {
        is: (val: string) => val === UserTypes.ENTREPRISE,
        then: (schema) =>
          schema.shape({
            pays: Yup.string().required("Le pays est obligatoire"),
            ville: Yup.string().required("La ville est obligatoire"),
            numeroRue: Yup.number()
              .nullable()
              .required("Le numéro de rue est obligatoire"),
          }),
        otherwise: (schema) =>
          schema.shape({
            intituleRue: Yup.string().required("L'adresse est obligatoire"),
          }),
      }),
    villeNaissance: Yup.string()
      .required("La ville de naissance est obligatoire")
      .test("is-valid-city", "Ville de naissance non valide", (value) =>
        villes.includes(value || "")
      ),
    villeActivite: Yup.string()
      .required("La ville d'activité est obligatoire")
      .test("is-valid-city", "Ville d'activité non valide", (value) =>
        villes.includes(value || "")
      ),
  });

interface UserFormProps {
  initialValue?: Partial<UserModel> | null;
  onClose?: () => void;
  authorizedTypes?: UserTypes[];
}

const UserForm: React.FC<UserFormProps> = ({
  initialValue,
  onClose,
  authorizedTypes,
}) => {
  const { error, setError, getFieldErrors, getFieldsErrors } =
    useYupValidationErrors();
  const { cities, fetchCities } = useCities();
  const { getAvailableTypes, saveUser, updateUser, saving } = useUsers();

  const getAuthorizedAvailableTypes = () => {
    if (!authorizedTypes || authorizedTypes.length === 0)
      return getAvailableTypes();
    return getAvailableTypes().filter((type) => authorizedTypes.includes(type));
  };

  const [dateNaissance, setDateNaissance] = useState<Date | null>(null);
  const [villeNaissance, setVilleNaissance] = useState(
    initialValue?.villeNaissance || ""
  );
  const [villeActivite, setVilleActivite] = useState(
    initialValue?.villeActivite || ""
  );
  const [nom, setNom] = useState(initialValue?.nom || "");
  const [prenom, setPrenom] = useState(initialValue?.prenom || "");
  const [email, setEmail] = useState(initialValue?.email || "");
  const [phone, setPhone] = useState("");
  const [intituleRue, setIntituleRue] = useState(
    initialValue?.adresse?.intituleRue || ""
  );
  const [typeUser, setTypeUser] = useState(
    initialValue?.type || UserTypes.PARTICULIER
  );
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  useEffect(() => {
    if (initialValue) {
      setNom(initialValue.nom || "");
      setPrenom(initialValue.prenom || "");
      setEmail(initialValue.email || "");
      setPhone(
        initialValue.phone
          ? initialValue.phone.replace(PHONE_PREFIX, "").trim()
          : ""
      );
      setIntituleRue(initialValue.adresse?.intituleRue || "");
      setVilleNaissance(initialValue.villeNaissance || "");
      setVilleActivite(initialValue.villeActivite || "");
      setTypeUser(initialValue.type || UserTypes.PARTICULIER);
      if (initialValue?.dateNaissance) {
        setDateNaissance(new Date(initialValue?.dateNaissance));
      }
    }
  }, [initialValue]);

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    checkFormValidity();
  }, [
    villeNaissance,
    villeActivite,
    nom,
    prenom,
    email,
    phone,
    dateNaissance,
    typeUser,
  ]);

  const getFormData = () => {
    const TheFormElement = document.querySelector(
      ".user-form"
    ) as HTMLFormElement;

    if (!TheFormElement) return null;

    return {
      type: typeUser || UserTypes.PARTICULIER,
      nom: nom || "",
      prenom: prenom || "",
      email: email || "",
      phone: phone ? `${PHONE_PREFIX} ${phone}` : "",
      dateNaissance: dateNaissance,
      adresse: {
        pays: "Sénégal",
        cp: "115",
        ville: villeActivite,
        intituleRue: intituleRue || "",
        numeroRue: 0,
      },
      villeNaissance,
      villeActivite,
    };
  };

  const checkFormValidity = async () => {
    const schema = validationSchema(cities);
    const params = getFormData();
    try {
      const res = await schema.validate(params ?? {}, { abortEarly: false });
      setIsFormValid(true);
      setError(null);
    } catch (err: any) {
      setIsFormValid(false);
      setError(err);
      // useToast(getFieldsErrors().join(", "), { variant: "error" });
    }
  };

  const handleSubmit = async () => {
    checkFormValidity();
    if (!isFormValid) {
      useToast("Veuillez corriger les erreurs du formulaire", {
        variant: "error",
      });
      useToast(getFieldErrors("nom").join(", "), { variant: "error" });
      return;
    }
    const params = getFormData();
    const response = await saveUser({
      ...params,
      dateNaissance: dateNaissance
        ? formatDateToDB(dateNaissance || new Date())
        : "",
    });
    const { success } = response ?? {};
    if (!success) {
      return;
    }
    if (onClose) onClose();
  };

  const handleUpdate = async () => {
    checkFormValidity();
    if (!isFormValid) {
      useToast("Veuillez corriger les erreurs du formulaire", {
        variant: "error",
      });
      useToast(getFieldErrors("nom").join(", "), { variant: "error" });
      return;
    }
    const params = getFormData();
    if (!params) {
      useToast("Les données du formulaire sont invalides", {
        variant: "error",
      });
      return;
    }
    const parseData: Partial<UserModel> = {
      ...initialValue,
      ...params,
      dateNaissance: dateNaissance
        ? formatDateToDB(dateNaissance || new Date())
        : "",
      dateMiseAJour: formatDateToFrench(
        new Date(initialValue?.dateMiseAJour || ""),
        "dd/MM/yyyy HH:mm"
      ),
      adresse: {
        ...(params?.adresse ?? {}),
        adresseComplete: params?.adresse?.intituleRue || "",
      },
    };
    const response = await updateUser(parseData);
    const { success } = response ?? {};
    if (!success) {
      return;
    }
    if (onClose) onClose();
  };

  return (
    <div>
      <h2 className="form-title">
        {initialValue
          ? `Modification de l'utisateur ${
              initialValue?.prenom || "Non défini"
            }`
          : "Ajouter un nouvel utilisateur"}
      </h2>
      <form className="user-form mt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
          <UiAdvancedSelect
            className="w-full relative"
            label="Type d'utilisateur"
            name="type"
            loading={false}
            defaultValue={typeUser}
            placeholder="Choisissez une option"
            styles={{ zIndex: 9999 }}
            onChange={(value) => {
              setTypeUser(value || "");
              checkFormValidity();
            }}
            options={getAuthorizedAvailableTypes().map((type) => ({
              value: type,
              label: type.replace(/_/g, " "),
            }))}
          ></UiAdvancedSelect>
        </div>

        <div className="grid gap-4 gap-y-2 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          <div className="w-full ">
            <UiInput
              label="Prénom"
              placeholder="Prénom"
              className="w-full ui-form-input"
              name="prenom"
              defaultValue={initialValue?.prenom ?? ""}
              onChange={(v: ChangeEvent<HTMLInputElement>) => {
                setPrenom(v.target.value);
              }}
            ></UiInput>
          </div>

          <div className="w-full ">
            <UiInput
              label="Nom"
              placeholder="Nom"
              className="w-full ui-form-input"
              name="nom"
              defaultValue={initialValue?.nom ?? ""}
              onChange={(v: ChangeEvent<HTMLInputElement>) => {
                setNom(v.target.value);
              }}
            ></UiInput>
          </div>

          <div className="w-full ">
            <UiDatepicker
              name="dateNaissance"
              className="w-full "
              label="Date de naissance"
              defaultValue={dateNaissance || null}
              shouldDisableDate={(date) => false}
              onChange={(v: Date | null) => {
                setDateNaissance(v ?? null);
              }}
            />
          </div>

          <div className="w-full ">
            <UiInput
              label="Adresse"
              placeholder="Adresse"
              className="w-full ui-form-input"
              name="adresse"
              defaultValue={initialValue?.adresse?.adresseComplete ?? ""}
              onChange={(v: ChangeEvent<HTMLInputElement>) => {
                setIntituleRue(v.target.value);
              }}
            ></UiInput>
          </div>

          <div className="w-full ">
            <UiInput
              label="Numéro de téléphone"
              placeholder="Numéro de téléphone"
              className="w-full ui-form-input"
              name="phone"
              defaultValue={phone || ""}
              onChange={(v: ChangeEvent<HTMLInputElement>) => {
                setPhone(v.target.value);
              }}
            ></UiInput>
          </div>

          <div className="w-full ">
            <UiInput
              label="Email"
              placeholder="Email"
              className="w-full ui-form-input"
              name="email"
              defaultValue={initialValue?.email ?? ""}
              onChange={(v: ChangeEvent<HTMLInputElement>) => {
                setEmail(v.target.value);
              }}
            ></UiInput>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4 mt-4">
          <UiAdvancedSelect
            className="w-full relative"
            label="Ville de naissance"
            name="villeNaissance"
            loading={false}
            defaultValue={villeNaissance}
            placeholder="Choisissez une option"
            styles={{ zIndex: 9999 }}
            onChange={(value) => {
              setVilleNaissance(value || "");
            }}
            options={cities.map((ville) => ({
              value: ville,
              label: ville,
            }))}
          ></UiAdvancedSelect>

          <UiAdvancedSelect
            className="w-full"
            label="Ville d'activité"
            name="villeActivite"
            loading={false}
            defaultValue={villeActivite}
            styles={{ zIndex: 9999 }}
            placeholder="Choisissez une option"
            onChange={(value) => {
              setVilleActivite(value || "");
            }}
            options={cities.map((ville) => ({
              value: ville,
              label: ville,
            }))}
          ></UiAdvancedSelect>
        </div>

        {error && (
          <div className="mt-4 bg-red-100 p-4 rounded">
            {getFieldsErrors().map((error) => (
              <div key={error} className="text-red-500">
                {error}
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <UiButton
            onClick={initialValue ? handleUpdate : handleSubmit}
            disabled={!isFormValid}
            loading={saving}
          >
            {initialValue ? "Modifier" : "Ajouter"}
          </UiButton>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
